/**
 * Attribution for the freezes the Long Task API reports as `unknown`.
 *
 * `PerformanceObserver({ type: "longtask" })` says WHEN the main thread was
 * blocked and for how long, never by what: bundled code carries no attribution.
 * This pairs each long task with the Redux actions whose dispatch overlapped
 * its window, which is the missing half — a freeze driven by a reducer, a
 * selector storm or a re-render cascade always runs inside a dispatch.
 *
 * Costs nothing unless the operator asked for it: with the flag off the
 * middleware is never installed and no observer is created. Turn it on with
 *
 *     localStorage.setItem("gantt-perf-debug", "1")   // then reload
 *
 * and read the report from the console with `__offolioPerf()`.
 */
import { isGanttPerfDebugEnabled } from "./ganttPerfDebug";

// Ring-buffer caps. A freeze worth chasing is seconds of dispatches, not tens
// of thousands, and an unbounded buffer would itself become the leak.
const MAX_EXECUTIONS = 5000;
const MAX_LONG_TASKS = 200;

// Chrome reports a task as "long" from 50 ms. Kept as the floor for what the
// report lists, so a healthy session produces an empty table rather than noise.
const LONG_TASK_FLOOR_MS = 50;

const push = (buffer, entry, cap) => {
  buffer.push(entry);
  if (buffer.length > cap) buffer.splice(0, buffer.length - cap);
};

const round = (value) => Math.round(value * 10) / 10;

/**
 * @param now injectable clock so the accounting is testable without timers.
 */
export const createPerfProfiler = ({ now = () => performance.now() } = {}) => {
  const executions = [];
  const longTasks = [];
  const totals = new Map();

  // A thunk reaches the middleware as a function, not an action object. Its
  // synchronous body is what blocks, so it is timed like any other dispatch
  // and named after itself when it has a name.
  const nameOf = (action) => {
    if (typeof action === "function") return `(thunk ${action.name || "anonymous"})`;
    if (action && typeof action.type === "string") return action.type;
    return "(unknown)";
  };

  const middleware = () => (next) => (action) => {
    const name = nameOf(action);
    const start = now();
    try {
      return next(action);
    } finally {
      const end = now();
      push(executions, { name, start, end }, MAX_EXECUTIONS);
      const stat = totals.get(name) || { count: 0, total: 0, max: 0 };
      const duration = end - start;
      stat.count += 1;
      stat.total += duration;
      stat.max = Math.max(stat.max, duration);
      totals.set(name, stat);
    }
  };

  const recordLongTask = ({ startTime, duration }) =>
    push(longTasks, { startTime, duration }, MAX_LONG_TASKS);

  // Overlap, not containment: the dispatch that blocks usually starts before
  // the browser opens the long-task window and ends after it closes.
  const actionsDuring = ({ startTime, duration }) => {
    const end = startTime + duration;
    const overlapping = executions.filter((e) => e.end > startTime && e.start < end);
    const byName = new Map();
    overlapping.forEach(({ name, start, end: stop }) => {
      const stat = byName.get(name) || { name, count: 0, total: 0 };
      stat.count += 1;
      stat.total += stop - start;
      byName.set(name, stat);
    });
    return [...byName.values()].sort((a, b) => b.total - a.total);
  };

  const report = () => ({
    longTasks: longTasks
      .filter((task) => task.duration >= LONG_TASK_FLOOR_MS)
      .sort((a, b) => b.duration - a.duration)
      .map((task) => ({
        startedAt: round(task.startTime),
        duration: round(task.duration),
        actions: actionsDuring(task),
      })),
    byAction: [...totals.entries()]
      .map(([name, stat]) => ({
        name,
        count: stat.count,
        total: round(stat.total),
        max: round(stat.max),
      }))
      .sort((a, b) => b.total - a.total),
    dispatches: [...totals.values()].reduce((sum, stat) => sum + stat.count, 0),
  });

  return { middleware, recordLongTask, report };
};

/* eslint-disable no-console */
export const printReport = (result, log = console) => {
  log.log(`=== ${result.dispatches} dispatches Redux ===`);

  log.log(`=== TACHES LONGUES : ${result.longTasks.length} ===`);
  result.longTasks.slice(0, 10).forEach((task) => {
    log.log(`--- ${task.duration} ms à t=${task.startedAt} ms ---`);
    log.table(
      task.actions.slice(0, 10).map((action) => ({
        action: action.name,
        appels: action.count,
        "cumul (ms)": round(action.total),
      }))
    );
  });

  log.log("=== ACTIONS LES PLUS COUTEUSES (cumul) ===");
  log.table(
    result.byAction.slice(0, 20).map((action) => ({
      action: action.name,
      appels: action.count,
      "cumul (ms)": action.total,
      "pire (ms)": action.max,
    }))
  );
};
/* eslint-enable no-console */

/**
 * Builds the profiler and wires it to the browser, when the flag is on.
 * Returns the Redux middleware, or null when profiling is off — the caller
 * must not install anything in that case.
 */
export const installPerfProfiler = () => {
  if (!isGanttPerfDebugEnabled()) return null;

  const profiler = createPerfProfiler();

  try {
    const observer = new PerformanceObserver((list) =>
      list.getEntries().forEach((entry) => profiler.recordLongTask(entry))
    );
    // `buffered` replays the long tasks that fired before this ran, so a freeze
    // during startup is captured even though the store is built after it.
    observer.observe({ type: "longtask", buffered: true });
  } catch {
    // Long Task is Chromium-only; the per-action totals stay useful without it.
  }

  window.__offolioPerf = () => {
    const result = profiler.report();
    printReport(result);
    return result;
  };

  return profiler.middleware;
};
